import React, { useState, useEffect } from 'react';

import { ConstantInputComponent } from 'main/components/ContactInputComponent';

import contactBg from '../../../main/assets/images/auvsi/contact.background-section.webp';
import contactTabletBg from '../../../main/assets/images/auvsi/contact-background-section-tablet.webp';
import contactMobileBg from '../../../main/assets/images/auvsi/contact-background-section-mobile.webp';

const ContactBackgroundImage = () => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  let currentBg = contactBg;
  if (windowWidth <= 1200) {
    currentBg = contactTabletBg;
  }
  if (windowWidth <= 834) {
    currentBg = contactMobileBg;
  }

  return (
    <img
      src={currentBg}
      alt=""
      className="auvsi-contact-image"
      style={{
        minWidth: '100%',
        objectFit: 'cover',
        marginTop: '40px',
      }}
    />
  );
};

interface CheckboxOption {
  id: string;
  label: string;
  value: string;
}

const FullContactForm: React.FC<{ initialEmail: string }> = ({
  initialEmail,
}) => {
  const [selectedElement, setSelectedElement] = useState<null | string>(null);
  const [name, setName] = useState<string>('');
  const [email, setEmail] = useState<string>(initialEmail);
  const [country, setCountry] = useState<string>('');
  const [company, setCompany] = useState<string>('');
  const [yourRequest, setYourRequest] = useState<string>('');
  const [radioOption, setRadioOption] = useState<'custom' | 'all'>('custom');

  const [errors, setErrors] = useState<{ [key: string]: string } | null>(null);
  const [data, setData] = useState<boolean | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [requestError, setRequestError] = useState<string | null>(null);

  const checkboxOptions: CheckboxOption[] = [
    {
      id: 'checkbox1',
      label: 'Flight Controllers',
      value: 'Flight Controllers',
    },
    { id: 'checkbox3', label: 'ESCs', value: 'ESCs' },
    { id: 'checkbox4', label: 'Orqa DTK APB', value: 'Orqa DTK APB' },
    {
      id: 'checkbox5',
      label: 'Orqa 2814 Brushless Motor',
      value: 'Orqa 2814 Brushless Motor',
    },
    {
      id: 'checkbox6',
      label: 'Orqa Justice FPV Camera',
      value: 'Orqa Justice FPV Camera',
    },
    {
      id: 'checkbox7',
      label: 'Orqa MRM Dual Cam Kit',
      value: 'Orqa MRM Dual Cam Kit',
    },
    { id: 'checkbox8', label: 'Simulator', value: 'Simulator' },
    { id: 'checkbox2', label: 'Hybrid Series', value: 'Hybrid Series' },
    { id: 'checkbox9', label: 'MRM system', value: 'MRM system' },
  ];

  const handleCheckboxChange = (value: string) => {
    const currentRequests = yourRequest ? yourRequest.split(',') : [];
    let newRequests: string[];

    if (currentRequests.includes(value)) {
      newRequests = currentRequests.filter(item => item !== value);
    } else {
      newRequests = [...currentRequests, value];
    }

    const newRequestString = newRequests.join(',');
    setYourRequest(newRequestString);

    const allSelected = checkboxOptions.every(option =>
      newRequests.includes(option.value),
    );
    setRadioOption(allSelected ? 'all' : 'custom');
  };

  const handleRadioChange = (value: 'custom' | 'all') => {
    setRadioOption(value);
    if (value === 'all') {
      setYourRequest(checkboxOptions.map(option => option.value).join(','));
    } else {
      setYourRequest('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: { [key: string]: string } = {};

    if (!name) {
      newErrors.name = 'Name field is required';
    }

    if (!email) {
      newErrors.email = 'Email field is required';
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      newErrors.email = 'Value is not a valid email format';
    }

    if (!country) {
      newErrors.country = 'Country field is required';
    }

    if (!company) {
      newErrors.company = 'Company field is required';
    }
    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      setLoading(true);
      try {
        const response = await fetch(
          'https://orqafpvsimulator-license.com/api/v1/asda/contact',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              name,
              email,
              country,
              company,
              request: yourRequest,
            }),
          },
        );

        if (response.ok) {
          setData(true);
          setErrors(null);
          setRequestError(null);
        } else {
          setRequestError('Failed to send email');
          setData(null);
        }
      } catch (error) {
        setRequestError('Something went wrong, try again or try again later!');
        setData(null);
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (yourRequest) {
      const selectedCount = yourRequest.split(',').length;
      const allSelected = selectedCount === checkboxOptions.length;
      setRadioOption(allSelected ? 'all' : 'custom');
    } else {
      setRadioOption('custom');
    }
  }, [checkboxOptions.length, yourRequest]);

  return (
    <div className="auvsi-contact-main-section">
      <section className="auvsi-contact-section">
        <div className="auvsi-contact-content">
          <h2 className="auvsi-contact-title">Get in touch</h2>
          <p className="auvsi-contact-description">
            Connect with us. For assistance with products or support inquiries,
            reach out directly. Our team is ready to help you achieve peak FPV
            performance.
          </p>
          <form
            id="asda-form"
            className="auvsi-contact-form"
            onSubmit={handleSubmit}
          >
            <div className="asda__form-wrapper">
              <div className="auvsi-form-columns-container">
                {/* Left Column */}
                <div className="auvsi-form-column form-column-left">
                  <p className="auvsi-column-description">
                    Contact Information
                  </p>

                  <div className="auvsi-input-fields-container">
                    <ConstantInputComponent
                      setter={setName}
                      setSelectedElement={setSelectedElement}
                      selectedElement={selectedElement}
                      name={'Name'}
                      errors={errors}
                      getter={name}
                      required
                    />

                    <ConstantInputComponent
                      setter={setEmail}
                      setSelectedElement={setSelectedElement}
                      selectedElement={selectedElement}
                      name={'Email'}
                      errors={errors}
                      getter={email}
                      required
                    />

                    <ConstantInputComponent
                      setter={setCountry}
                      setSelectedElement={setSelectedElement}
                      selectedElement={selectedElement}
                      name={'Country'}
                      errors={errors}
                      getter={country}
                      required
                    />

                    <ConstantInputComponent
                      setter={setCompany}
                      setSelectedElement={setSelectedElement}
                      selectedElement={selectedElement}
                      name={'Company'}
                      errors={errors}
                      getter={company}
                      required
                    />
                  </div>
                </div>

                {/* Right Column */}
                <div className="auvsi-form-column form-column-right">
                  <p className="auvsi-column-description">
                    Select your interests
                  </p>

                  <div className="auvsi-radio-options">
                    <div className="auvsi-radio-option">
                      <input
                        type="radio"
                        id="customOption"
                        name="requestType"
                        checked={radioOption === 'custom'}
                        onChange={() => handleRadioChange('custom')}
                      />
                      <label htmlFor="customOption">Custom selection</label>
                    </div>
                    <div className="auvsi-radio-option">
                      <input
                        type="radio"
                        id="allOption"
                        name="requestType"
                        checked={radioOption === 'all'}
                        onChange={() => handleRadioChange('all')}
                      />
                      <label htmlFor="allOption">All options</label>
                    </div>
                  </div>

                  <div className="auvsi-checkbox-section">
                    <div className="auvsi-checkbox-columns">
                      <div className="auvsi-checkbox-column">
                        {checkboxOptions.slice(0, 5).map(option => (
                          <div
                            key={option.id}
                            className="auvsi-checkbox-option"
                          >
                            <input
                              type="checkbox"
                              id={option.id}
                              checked={yourRequest.includes(option.value)}
                              onChange={() =>
                                handleCheckboxChange(option.value)
                              }
                            />
                            <label htmlFor={option.id}>{option.label}</label>
                          </div>
                        ))}
                      </div>
                      <div className="auvsi-checkbox-column">
                        {checkboxOptions.slice(5).map(option => (
                          <div
                            key={option.id}
                            className="auvsi-checkbox-option"
                          >
                            <input
                              type="checkbox"
                              id={option.id}
                              checked={yourRequest.includes(option.value)}
                              onChange={() =>
                                handleCheckboxChange(option.value)
                              }
                            />
                            <label htmlFor={option.id}>{option.label}</label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="auvsi-form-status-message">
              {data && !requestError && !errors && (
                <p className="auvsi-success-message">
                  Email Successfully sent!
                </p>
              )}
              {requestError && (
                <p className="auvsi-error-message">
                  Something went wrong, try again or try again later!
                </p>
              )}
            </div>

            <div className="auvsi-contact-submit-container">
              <button
                type="submit"
                disabled={loading}
                className="asda__submit-button"
                style={{ margin: 'auto' }}
              >
                {loading ? (
                  <div className="asda__button-spinner--wrapper">
                    <div className="asda__button-spinner" />
                  </div>
                ) : (
                  <p>get in touch</p>
                )}
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export const Contact = () => {
  const [showFullForm, setShowFullForm] = useState(false);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      setEmailError('Email field is required');
      return;
    } else if (!emailRegex.test(email)) {
      setEmailError('Value is not a valid email format');
      return;
    }

    setEmailError('');
    setIsSubmitting(true);

    try {
      const response = await fetch(
        'https://orqafpvsimulator-license.com/api/v1/asda/contact',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: email,
          }),
        },
      );

      if (response.ok) {
        setShowFullForm(true);
      } else {
        setEmailError('Failed to submit email. Please try again.');
      }
    } catch (error) {
      setEmailError('Failed to submit email. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showFullForm) {
    return <FullContactForm initialEmail={email} />;
  }

  return (
    <div
      className="auvsi-contact-main-section"
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      <section className="auvsi-contact-section">
        <div className="auvsi-contact-content">
          <h2 className="auvsi-contact-title">Get in touch</h2>
          <p className="auvsi-contact-description">
            Connect with us. For assistance with products or support inquiries,
            reach out directly. Our team is ready to help you achieve peak FPV
            performance.
          </p>

          <form onSubmit={handleEmailSubmit} style={{ minWidth: '100%' }}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: emailError !== '' ? '30px' : '40px',
                alignSelf: 'stretch',
              }}
            >
              <div className="auvsi-contact-input-container">
                <ConstantInputComponent
                  name="Email"
                  setter={setEmail}
                  getter={email}
                  errors={emailError ? { email: emailError } : null}
                  setSelectedElement={() => null}
                  selectedElement={null}
                  required
                />
              </div>
              <button
                disabled={isSubmitting}
                className="asda__submit-button auvsi-contact-button"
                type="submit"
              >
                <>
                  {isSubmitting ? (
                    <div className=" asda__button-spinner--wrapper">
                      <div className="asda__button-spinner" />
                    </div>
                  ) : (
                    <p>get in touch</p>
                  )}
                </>
              </button>
            </div>
          </form>
        </div>
      </section>
      <ContactBackgroundImage />
    </div>
  );
};
